"use client";

import { useEffect, useState } from "react";
import Navbar from "@/components/shared/navbar/navbar";
import Footer from "@/components/shared/footer";
import LoadingScreen from "@/components/shared/loading-screen";
import {
	authFetch,
	fetchSessionUser,
	mightHaveAuthSession,
} from "@/lib/authClient";
import { Circolare } from "@/types/circolari";
import SectionTitle from "@/components/shared/SectionTitle";
import CircolariSection from "@/components/documentazione/circolari/circolari-section";
import ReservedEmptyState from "@/components/shared/reserved-empty-shared";
import PageVisibilityGuard from "@/components/shared/page-visibility-guard";

export default function Circolari() {
	const [showLoader, setShowLoader] = useState(true);
	const [loading, setLoading] = useState(true);
	const [circolari, setCircolari] = useState<Circolare[]>([]);
	const [isAdmin, setIsAdmin] = useState(false);
	const [isAuthenticated, setIsAuthenticated] = useState(false);

	useEffect(() => {
		let alive = true;

		(async () => {
			try {
				await new Promise((res) => setTimeout(res, 400));

				const user = mightHaveAuthSession()
					? await fetchSessionUser({ clearStale: true })
					: null;

				if (!alive) return;

				if (!user) {
					setIsAuthenticated(false);
					setIsAdmin(false);
					setCircolari([]);
					return;
				}

				setIsAuthenticated(true);
				setIsAdmin(user.role === "admin");

				const res = await authFetch("/api/circolari", {
					method: "GET",
					cache: "no-store",
				});

				if (!alive) return;

				if (res.status === 401 || res.status === 403) {
					setIsAuthenticated(false);
					setIsAdmin(false);
					setCircolari([]);
					return;
				}

				if (!res.ok) throw new Error("Errore nel caricamento delle circolari");

				const data = await res.json();
				if (!alive) return;

				const mapped: Circolare[] = (data || []).map(
					(c: Record<string, unknown>) => ({
						_id: String(c._id ?? ""),
						title: String(c.title ?? ""),
						description: String(c.description ?? ""),
						date: String(c.date ?? ""),
						pdf: String(c.pdf ?? c.fileUrl ?? ""),
						fileUrl: String(c.fileUrl ?? c.pdf ?? ""),
					}),
				);

				setCircolari(mapped);
			} catch {
				if (!alive) return;
				setCircolari([]);
			} finally {
				if (!alive) return;
				setLoading(false);
				setTimeout(() => setShowLoader(false), 300);
			}
		})();

		const onStorage = (e: StorageEvent) => {
			if (e.key === "logout" || e.key === "auth_session") {
				window.location.reload();
			}
		};
		const onAuthChanged = () => window.location.reload();
		window.addEventListener("storage", onStorage);
		window.addEventListener("auth:changed", onAuthChanged);

		return () => {
			alive = false;
			window.removeEventListener("storage", onStorage);
			window.removeEventListener("auth:changed", onAuthChanged);
		};
	}, []);

	if (showLoader) {
		return (
			<PageVisibilityGuard pageKey="circolari">
				<LoadingScreen show={true} fullscreen bgColor="#fff" />
			</PageVisibilityGuard>
		);
	}

	return (
		<PageVisibilityGuard pageKey="circolari">
			<main className="bg-(--bg-secondary)">
				<Navbar />
				{loading ? (
					<SectionTitle title1="Caricamento " title2="circolari..." />
				) : !isAuthenticated ? (
					<ReservedEmptyState
						title="Accesso richiesto"
						description="Per consultare le circolari devi effettuare l'accesso all'area riservata."
						buttonLabel="Vai all'area riservata"
						href="/area-riservata"
					/>
				) : circolari.length > 0 ? (
					<CircolariSection circolari={circolari} isAdmin={isAdmin} />
				) : (
					<ReservedEmptyState
						title="Nessuna circolare"
						description="Al momento non ci sono circolari disponibili."
						buttonLabel="Torna alla home"
						href="/"
					/>
				)}
				<Footer />
			</main>
		</PageVisibilityGuard>
	);
}
